import { env } from '~/env.ts'
import WebViewer, { WebViewerInstance } from '@pdftron/webviewer'
import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router'
import ConfirmProductRequirementsTable from '~/components/Submittals/ConfirmProductRequirementsTable'
import { setDefaultViewerSettings } from '../ScheduleSelection/ScheduleSelection'
import { headerCustomizations } from '~/utilities/webviewer/customizations'
import { JobManifest } from '~/graphql/hooks/useJobManifestQuery'
import { useQuery } from '@tanstack/react-query'
import { getWorkflowStepResult } from '~/lib/mbue-api/services/workflowStepResult'
import { WorkflowStep3Result } from '~/lib/mbue-api/types'
import { useFetchDocList, useFetchWorkflow } from '~/hooks/submittals'
import Loading from '~/components/ui/Loading'
import { toast } from 'sonner'
import { WorkflowStepStatus } from 'mbue-rest-client'
import { useFlag } from '~/hooks/useFlag'
import { getFirefoxVersion } from '~/utilities/utils'

export default function ConfirmProductRequirements() {
  const viewerDivRef = useRef<HTMLDivElement>(null)

  const isViewerInitialized = useRef<boolean>(false)

  const [isDocumentLoading, setIsDocumentLoading] = useState<boolean>(true)

  const [selectedRequirement, setSelectedRequirement] = useState<{
    specificationIndex: number
    requirementIndex: number
  } | null>(null)

  const isProdViewerEnabled = useFlag('apryse-prod-viewer-enabled')

  const { projectId, workflowInstanceId } = useParams<{
    projectId: string
    workflowInstanceId: string
  }>()

  const { data: workflowWithData, isLoading: workflowWithDataIsLoading } =
    useFetchWorkflow({
      projectId: parseInt(projectId!, 10),
      workflowInstanceId: workflowInstanceId!
    })

  const { data: docList, isLoading: isLoadingDocList } = useFetchDocList(
    { projectId: parseInt(projectId!, 10) },
    {
      refetchInterval: false,
      retry: false
    }
  )

  const { data: workflowStep3Result, isLoading: isWorkflowStep3ResultLoading } = useQuery(
    {
      queryKey: ['workflowStepProductRequirementsResult', projectId, workflowInstanceId],
      queryFn: () => {
        return getWorkflowStepResult({
          projectId: Number(projectId!),
          workflowInstanceId: workflowInstanceId!,
          stepIndex: 3
        })
      },
      enabled: !!projectId && !!workflowInstanceId,
      retry: false
    }
  )

  useEffect(() => {
    if (isLoadingDocList || isWorkflowStep3ResultLoading) return
    if (isProdViewerEnabled === null) return

    let webviewerKey: string = env.VITE_WEBVIEWER_DEMO_TOKEN ?? ''

    if (isProdViewerEnabled && env.VITE_WEBVIEWER_TOKEN) {
      webviewerKey = env.VITE_WEBVIEWER_TOKEN
    }

    const documentUri = docList?.specification_doc.download_url ?? ''

    let webviewerInstance: WebViewerInstance

    const initWebviewer = async () => {
      if (isViewerInitialized.current || webviewerInstance) return

      isViewerInitialized.current = true

      try {
        webviewerInstance = await WebViewer.Iframe(
          {
            path: '/webviewer-0ad25b9a',
            licenseKey: webviewerKey,
            preloadWorker: 'pdf',
            fullAPI: true,
            ui: 'legacy'
          },
          viewerDivRef.current!
        )

        const { Core, UI } = webviewerInstance
        const { documentViewer } = Core

        const version = getFirefoxVersion()

        if (version && version > 145) {
          console.log('Disabling virtual display mode for Firefox version', version)
          const displayMode = Core.documentViewer.getDisplayModeManager()
          displayMode.disableVirtualDisplayMode()
        }

        documentViewer.addEventListener('documentLoaded', () => {
          setDefaultViewerSettings(webviewerInstance)
          headerCustomizations(
            webviewerInstance,
            { matches: [] } as unknown as JobManifest,
            'product-requirements',
            documentViewer.getPageCount(),
            { isLayerToggleEnabled: false }
          )

          UI.disableFeatures([UI.Feature.Annotations, UI.Feature.NotesPanel])
          setIsDocumentLoading(false)
        })

        void documentViewer.loadDocument(documentUri, { extension: 'pdf' })
      } catch (error) {
        console.error('Error initializing WebViewer:', error)
      }
    }

    void initWebviewer()
  }, [isProdViewerEnabled, docList, isLoadingDocList, isWorkflowStep3ResultLoading])

  useEffect(() => {
    if (!viewerDivRef.current) return
    if (!window.WebViewer) return
    if (selectedRequirement === null || !workflowStep3Result) return

    const productRequirements = Array.isArray(workflowStep3Result)
      ? (workflowStep3Result[0] as unknown as WorkflowStep3Result)
      : (workflowStep3Result as unknown as WorkflowStep3Result)

    const specification =
      productRequirements.requirements_list.rows[selectedRequirement.specificationIndex]

    const requirement = specification.requirements[selectedRequirement.requirementIndex]

    const instance = window.WebViewer.getInstance(viewerDivRef.current)
    const { Core } = instance
    const { annotationManager, Annotations } = Core

    const annotations = annotationManager.getAnnotationsList()
    if (annotations.length > 0) {
      annotationManager.deleteAnnotations(annotations, { imported: false, force: true })
    }

    if (requirement.points.length === 0) return

    requirement.points.forEach((point) => {
      const annotation = new Annotations.RectangleAnnotation({
        PageNumber: point.source_page,
        X: point.coordinates.xmin,
        Y: point.coordinates.ymin,
        Width: point.coordinates.xmax - point.coordinates.xmin,
        Height: point.coordinates.ymax - point.coordinates.ymin,
        StrokeColor: new Annotations.Color(100, 180, 220, 0.8),
        FillColor: new Annotations.Color(100, 180, 220, 0.25),
        Author: 'mbue',
        ReadOnly: true,
        SelectionEnabled: false
      })

      annotationManager.addAnnotation(annotation)
      annotationManager.redrawAnnotation(annotation)
    })

    Core.documentViewer.setCurrentPage(requirement.points[0].source_page, true)
  }, [selectedRequirement, workflowStep3Result])

  const hasShownCompletionToastRef = useRef(false)

  useEffect(() => {
    if (workflowWithDataIsLoading) return

    if (
      !workflowWithDataIsLoading &&
      workflowWithData?.steps?.[3]?.state?.status === WorkflowStepStatus.COMPLETED &&
      !hasShownCompletionToastRef.current
    ) {
      toast.success('Step Already Completed', {
        description: 'This is a read only version of the product requirements'
      })
      hasShownCompletionToastRef.current = true
    }
  }, [workflowWithData, workflowWithDataIsLoading])

  const isLoading = isDocumentLoading || isLoadingDocList || isWorkflowStep3ResultLoading

  const isStepCompleted =
    workflowWithData?.steps?.[3]?.state?.status === WorkflowStepStatus.COMPLETED

  return (
    <>
      {isLoading && <Loading mode="dark" position="fixed" />}
      <div className="w-full h-full flex flex-col">
        <div className="h-[calc(100vh-56px)] w-full grid grid-cols-2">
          <div className="h-full viewer" ref={viewerDivRef} />

          {/* Confirmation section */}

          <ConfirmProductRequirementsTable
            viewerRef={viewerDivRef}
            workflowStep3Result={workflowStep3Result as unknown as WorkflowStep3Result}
            selectedRequirement={selectedRequirement}
            setSelectedRequirement={setSelectedRequirement}
            setIsDocumentLoading={setIsDocumentLoading}
            isStepCompleted={isStepCompleted}
          />
        </div>
      </div>
    </>
  )
}
